import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Title, Text, TextInput, Button, Select, SelectItem } from '@tremor/react';
import { api } from '../../lib/api';

interface NewClassifierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (key: string) => void;
}

// Classifier keys must match ^[a-zA-Z][a-zA-Z0-9]*$ (camelCase, no separators).
// If the name contains separators (space/dash/_/etc.), split and camelCase each chunk
// (lowercase the first chunk entirely to keep acronyms like "RC" intact at the start).
// Otherwise (single token), just lowercase the leading character.
function toCamelCaseKey(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/[^a-zA-Z0-9]/.test(trimmed)) {
    const parts = trimmed.split(/[^a-zA-Z0-9]+/).filter(Boolean);
    if (parts.length === 0) return '';
    return parts
      .map((part, i) =>
        i === 0
          ? part.toLowerCase()
          : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      )
      .join('');
  }
  return trimmed.charAt(0).toLowerCase() + trimmed.slice(1);
}

export function NewClassifierModal({ isOpen, onClose, onSuccess }: NewClassifierModalProps) {
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [name, setName] = useState('');
  const [classifierType, setClassifierType] = useState('sentence');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setKey('');
      setKeyTouched(false);
      setName('');
      setClassifierType('sentence');
      setDescription('');
      setError(null);
    }
  }, [isOpen]);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!keyTouched) {
      setKey(toCamelCaseKey(value));
    }
  };

  const handleKeyChange = (value: string) => {
    setKey(value);
    setKeyTouched(true);
  };

  const handleSubmit = async () => {
    if (!key.trim() || !name.trim()) {
      setError('Key and name are required');
      return;
    }

    if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(key)) {
      setError('Key must start with a letter and contain only letters and numbers');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const trimmedKey = key.trim();
      await api.createClassifier({
        key: trimmedKey,
        name: name.trim(),
        classifierType: classifierType as 'sentence' | 'document',
        displayName: name.trim(),
        description: description.trim() || undefined,
      });
      onSuccess(trimmedKey);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create classifier');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} static={true}>
      <DialogPanel className="bg-gray-900 border border-gray-800 max-w-md">
        <Title className="text-white">New Classifier Family</Title>
        <Text className="text-gray-400 mt-1">
          Create a new classifier. You'll add model versions after creation.
        </Text>

        <div className="mt-4 space-y-4">
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Name</label>
            <TextInput
              placeholder="e.g., RcSentiment, FairHousing"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Key</label>
            <TextInput
              placeholder="e.g., sentiment, fairHousing"
              value={key}
              onChange={(e) => handleKeyChange(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Stable identifier used in API calls. Cannot be changed later.</p>
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Type</label>
            <Select value={classifierType} onValueChange={setClassifierType} className="mt-1">
              <SelectItem value="sentence">Sentence-level</SelectItem>
              <SelectItem value="document">Document-level</SelectItem>
            </Select>
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide">Description (optional)</label>
            <TextInput
              placeholder="What this classifier detects"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1"
            />
          </div>

          {error && (
            <div className="bg-red-950/50 border border-red-900 rounded p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isLoading} disabled={!key.trim() || !name.trim()}>
            Create Classifier
          </Button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
